<template>
  <main v-if="data">
    <TContainer class="py-10 md:py-16">
      <TGrid has-vertical-gap>
        <TGridItem :md="4">
          <div class="border border-gray-200 rounded-lg p-6">
            <div class="text-center">
              <div>
                <button @click="avatarButtonClick">
                  <TAvatar
                    :src="avatar"
                    :text="canEdit ? undefined : userInitials"
                    :alt="fullName"
                    size="6xl"
                    :is-super-host="isSuperhost"
                    :class="{ 'cursor-pointer': canEdit }"
                  />
                </button>

                <AppChangeAvatarButton
                  v-if="canEdit"
                  ref="avatarButton"
                  class="mt-2"
                  :is-change-photo="avatar !== null"
                  :is-loading="isLoading"
                  @avatar-upload-failure="avatarUploadFailure = $event"
                  @avatar-upload-success="updateAvatar"
                />
              </div>

              <TTipbox
                v-if="canEdit && avatarUploadFailure"
                variant="error"
                class="mb-4"
              >
                {{
                  t('errorUploadingImage')
                }}
              </TTipbox>

              <p class="mt-6 font-semibold text-2xl">
                {{ fullName }}
              </p>
            </div>

            <div class="grid gap-2 border-t border-gray-200 mt-6 pt-6">
              <template v-if="hasRVs">
                <TRating
                  v-if="data?.reviews?.RenterRating"
                  :rating="data?.reviews?.RenterRating"
                  :number-of-reviews="data?.reviews?.RenterReviews?.length ?? 0"
                  is-clickable
                  @click="scrollToReviews"
                />
              </template>
              <template v-else>
                <TRating
                  v-if="data?.reviews?.OwnerRating"
                  :rating="data?.reviews?.OwnerRating"
                  :number-of-reviews="data?.reviews?.OwnerReviews?.length ?? 0"
                  is-clickable
                  @click="scrollToReviews"
                />
              </template>

              <UserStats
                :is-verified="data?.profile?.IsVerified"
                :is-superhost="isSuperhost"
                :response-rate="data?.profile?.OwnerScore"
              />

              <I18nT
                v-if="data?.profile?.OwnerScore && canEdit && hasRVs"
                keypath="ownerImproveResponseRateTipbox.content"
                tag="span"
                class="text-sm text-gray-500"
              >
                <template #moreInfo>
                  <NuxtLink
                    :to="t('helpUrls.ownerResponseRate')"
                    :target="isWebView ? '_self' : '_blank'"
                    class="underline hover:no-underline"
                  >
                    {{ t('ownerImproveResponseRateTipbox.improveYourResponseRate') }}
                  </NuxtLink>
                </template>
              </I18nT>
            </div>
          </div>
        </TGridItem>

        <TGridItem :md="8">
          <div class="divide-y divide-gray-200">
            <div class="py-8 md:pt-0">
              <div class="flex gap-4 items-start justify-between">
                <div>
                  <THeading :level="5">
                    {{ t('about', { name: data?.profile?.FirstName }) }}
                  </THeading>

                  <TSubHeading v-if="data?.profile?.CreatedAt">
                    {{ t('joinedIn', { date: formatAsYearOnly(data?.profile?.CreatedAt) }) }}
                  </TSubHeading>
                </div>
                <ProfileEditBio v-if="canEdit" />
              </div>

              <div class="mt-8">
                <AppCollapsibleContent
                  v-if="bioContent"
                  :content="bioContent"
                  more-button-variant="link"
                />

                <div v-else-if="canEdit">
                  {{ t(`addBio.${hasRVs ? 'host' : 'guest'}`) }}
                </div>
              </div>
            </div>

            <div
              v-if="hasRVs"
              class="py-8 space-y-4"
            >
              <THeading
                :level="5"
                tag="h2"
              >
                {{ t('rvs') }}
              </THeading>

              <Grid
                :max-results="allRvs?.length ?? 0"
                :cols="2"
                :xl-cols="3"
              >
                <template #default="{ getItemClass }">
                  <CardRv
                    v-for="(rv, index) in allRvs"
                    :key="rv.Id"
                    :index="index"
                    search-request-id="none"
                    no-lazy-load
                    :rv="rv"
                    cta="profile"
                    :class="getItemClass(index)"
                    @visible:rv="queueViewableImpression"
                  />
                </template>
              </Grid>
            </div>

            <div
              id="reviews-container"
              ref="reviewsContainer"
              class="py-8"
            >
              <div v-if="renterReviews.length || ownerReviews.length">
                <div
                  v-if="renterReviews.length"
                  class="space-y-6"
                >
                  <THeading
                    :level="5"
                    tag="h2"
                  >
                    {{ t('fromGuests', { count: renterReviews.length }) }}
                  </THeading>

                  <AppCollapsibleRecords
                    v-slot="{ records }"
                    :records="renterReviews"
                    :quantity-to-show-when-collapsed="5"
                  >
                    <TReviewList :reviews="records" />
                  </AppCollapsibleRecords>
                </div>
                <div
                  v-if="ownerReviews.length"
                  class="space-y-6"
                >
                  <THeading
                    :level="5"
                    tag="h2"
                  >
                    {{ t('fromHosts', { count: ownerReviews.length }) }}
                  </THeading>
                  <AppCollapsibleRecords
                    v-slot="{ records }"
                    :records="ownerReviews"
                    :quantity-to-show-when-collapsed="5"
                  >
                    <TReviewList :reviews="records" />
                  </AppCollapsibleRecords>
                </div>
              </div>
              <div
                v-else
                class="space-y-6"
              >
                <THeading
                  :level="5"
                  tag="h2"
                >
                  {{ t('reviews') }}
                </THeading>
                <p>{{ t('noReviews') }}</p>
              </div>
            </div>
          </div>
        </TGridItem>
      </TGrid>
    </TContainer>
  </main>
</template>

<script setup lang="ts">
const { t } = useI18n()

const route = useRoute()
const { isLoggedIn } = useAuthentication()
const { userIsOwner, userAvatar, userAbout, userId } = useUser()
const { isWebView } = usePlatform()
const getRouteBaseName = useRouteBaseName()
const { formatAsYearOnly } = useDates()
const { $captureError } = useNuxtApp()

definePageMeta({
  validate: (route) => /^\d+$/.test((route.params.id as string) ?? ''),
  middleware: ['blank-layout'],

  i18n: {
    paths: {
      en: '/profile/[id]',
      fr: '/profil/[id]',
    },
  },
})

const profileId = computed(() => Number(route.params.id))

const { $rentals } = useNuxtApp()
const { data, error } = await useAsyncData(
  computed(() => `profile-${route.params.id}`),
  async () => {
    const [profile, rvs, reviews] = await Promise.all([
      $rentals('/api/user-profiles/profile-other', {
        params: {
          profileId: profileId.value,
        },
      }),
      $rentals('/api/rvlistings/rv-details-of-owner/{profileId}', {
        path: {
          profileId: profileId.value,
        },
      }),
      $rentals('/api/reviews/{userId}', {
        path: {
          userId: profileId.value,
        },
      }),
    ])

    return { profile, rvs, reviews }
  },
)

if (!data.value?.profile) {
  showError({
    statusCode: 404,
    statusMessage: `Profile not found: (${route.params.id})`,
  })
}

if (error.value) {
  showError({
    statusCode: error.value?.statusCode || 404,
    statusMessage: `There was an issue fetching this profile: (${route.params.id})`,
  })
}

useSeoMeta({
  title: data.value?.profile?.FirstName,
  robots: {
    noindex: true,
    follow: true,
  },
})

const avatarUploadFailure = ref(false)
const previousRoute = ref(undefined)
const cta = route.query.cta
const avatarButton = useTemplateRef('avatarButton')
const reviewsContainer = ref<HTMLDivElement>()

const canEdit = computed(() => isLoggedIn.value && userId.value === Number(route.params.id))
const fullName = computed(() => formatNameAsFullName(data.value?.profile?.FirstName, data.value?.profile?.LastName))
const userInitials = computed(() => formatNameAsInitials(data.value?.profile?.FirstName, data.value?.profile?.LastName))
const avatar = computed(() => (canEdit.value ? userAvatar.value : data.value?.profile?.Avatar))
const bioContent = computed(() => canEdit.value ? userAbout.value : data.value?.profile?.About || '')
const allRvs = computed(() => data.value?.rvs?.ListRVs || [])
const hasRVs = computed(() => allRvs.value.length > 0)
const isSuperhost = computed(() => data.value?.profile?.SuperHost?.IsActive)

const renterReviews = computed(() =>
  (data.value?.reviews?.RenterReviews?.map((review) => ({
    Id: review.Id,
    Content: review.Content ?? '',
    CreatedAt: review.CreatedAt,
    DaysBeforeTrip: review.DaysBeforeTrip,
    Reviewer: {
      Id: review.Reviewer,
      Avatar: review.ReviewerAvatarUrl,
      FirstName: review.ReviewerFirstName,
      LastName: review.ReviewerLastName,
      Initials: formatNameAsInitials(review.ReviewerFirstName, review.ReviewerLastName),
    },
    Rating: review.Rating,
    OverallRating: 0,
    ReviewType: review.ReviewType,
    RVDelivered: review.RVDelivered,
    Reply: review.Reply
      ? {
          Content: review.Reply.Content,
          RepliedAt: review.Reply.RepliedAt,
          Replier: review.Reply.Replier
            ? {
                FirstName: review.Reply.Replier.FirstName,
                LastName: review.Reply.Replier.LastName,
                Initials: formatNameAsInitials(review.Reply.Replier.FirstName, review.Reply.Replier.LastName),
                Avatar: review.Reply.Replier.Avatar,
                Id: review.Reply.Replier.Id,
              }
            : undefined,
        }
      : undefined,
    Photos: review.Photos,
  })) ?? []),
)

const ownerReviews = computed(() =>
  (data.value?.reviews?.OwnerReviews?.map((review) => ({
    Id: review.Id,
    Content: review.Content ?? '',
    CreatedAt: review.CreatedAt,
    DaysBeforeTrip: review.DaysBeforeTrip,
    Reviewer: {
      Id: review.Reviewer,
      Avatar: review.ReviewerAvatarUrl,
      FirstName: review.ReviewerFirstName,
      LastName: review.ReviewerLastName,
      Initials: formatNameAsInitials(review.ReviewerFirstName, review.ReviewerLastName),
    },
    Rating: review.Rating,
    OverallRating: 0,
    ReviewType: review.ReviewType,
    RVDelivered: review.RVDelivered,
    Reply: review.Reply
      ? {
          Content: review.Reply.Content,
          RepliedAt: review.Reply.RepliedAt,
          Replier: review.Reply.Replier
            ? {
                FirstName: review.Reply.Replier.FirstName,
                LastName: review.Reply.Replier.LastName,
                Initials: formatNameAsInitials(review.Reply.Replier.FirstName, review.Reply.Replier.LastName),
                Avatar: review.Reply.Replier.Avatar,
                Id: review.Reply.Replier.Id,
              }
            : undefined }
      : undefined,
    Photos: review.Photos,
  })) ?? []),
)

onMounted(async () => {
  const numberOfReviews = renterReviews.value.length + ownerReviews.value.length

  if (data.value?.profile) {
    trackProfileViewed({
      isOwner: userIsOwner.value,
      isHostProfile: hasRVs.value,
      receiverUserId: data.value?.profile.Id,
      isEditable: canEdit.value,
      pageSource: (previousRoute.value && getRouteBaseName(previousRoute.value)) || 'unknown',
      cta: (cta as string) || 'unknown',
      hasProfilePhoto: Boolean(data.value?.profile.Avatar),
      hasReviewsAverage: Boolean(hasRVs.value ? data.value?.reviews?.RenterRating : data.value?.reviews?.OwnerRating),
      hasVerificationBadge: Boolean(data.value?.profile.IsVerified),
      hasJoinedDate: Boolean(data.value?.profile.CreatedAt),
      ...(data.value?.profile.CreatedAt && {
        joinedYear: datetime(data.value?.profile.CreatedAt).year(),
      }),
      hasProfileDescription: Boolean(data.value?.profile.About),
      rvListingsDisplayed: allRvs.value.length,
      isSuperhost: data.value?.profile.SuperHost?.IsActive ?? false,
      numberOfReviews,
    })
  }
})

function scrollToReviews() {
  reviewsContainer.value?.scrollIntoView({ behavior: 'smooth' })
}

function avatarButtonClick() {
  if (!canEdit.value) return

  avatarButton.value?.openFileBrowserWindow()
}

const { getSession } = useAuth()

async function checkImageUrl(url: string) {
  try {
    const response = await fetch(url)
    return response.status === 200
  }
  catch {
    return false
  }
}

const isLoading = ref(false)

const { getDynamicImage, presets } = useImage()
const preset = presets.SQUARE_MD

async function updateAvatar(path: string) {
  const url = getDynamicImage({
    path,
    preset,
  })

  if (!url) {
    return
  }

  isLoading.value = true
  let attempts = 0
  const maxAttempts = 5

  while (attempts < maxAttempts) {
    const isLoaded = await checkImageUrl(url)

    if (isLoaded) {
      await getSession()
      isLoading.value = false
      return
    }

    attempts++
    if (attempts < maxAttempts)
      await new Promise((resolve) => {
        setTimeout(resolve, 3000)
      })
  }

  $captureError(new Error('Failed to load avatar after multiple attempts'))
  isLoading.value = false
}

const { queueViewableImpression } = useRvViewableImpressions({
  pageNumber: 1,
  pageSize: computed(() => allRvs.value.length),
})
</script>

<i18n lang="json">
{
  "en": {
    "about": "About {name}",
    "joinedIn": "Joined in {date}",
    "addBio": {
      "host": "Tell guests a bit about you, your interests, and what makes you a great host.",
      "guest": "Share a bit about yourself, what makes you a great guest, and any experience you may have with RVing."
    },
    "rvs": "RV Listings",
    "ownerImproveResponseRateTipbox": {
      "content": "Accept or decline booking requests within 48 hours to {moreInfo}!",
      "improveYourResponseRate": "improve your response rate"
    },
    "reviews": "Reviews",
    "fromHosts": "Reviews from hosts ({count})",
    "fromGuests": "Reviews from guests ({count})",
    "noReviews": "No reviews (yet!)",
    "errorUploadingImage": "Error uploading image"
  },
  "fr": {
    "about": "À propos de {name} | À propos d’{name}",
    "joinedIn": "Membre depuis {date}",
    "addBio": {
      "host": "Parlez un peu de vous, de vos intérêts et de ce qui fait de vous un excellent hôte.",
      "guest": "Parlez un peu de vous, de ce qui fait de vous un excellent invité et de toute expérience que vous pourriez avoir avec les VR."
    },
    "rvs": "Annonces de VR",
    "ownerImproveResponseRateTipbox": {
      "content": "Acceptez ou refusez les demandes de réservation dans les 48 heures pour {moreInfo}!",
      "improveYourResponseRate": "améliorer votre taux de réponse"
    },
    "reviews": "Commentaires",
    "fromHosts": "Commentaires des hôtes ({count})",
    "fromGuests": "Commentaires des voyageurs ({count})",
    "noReviews": "Pas encore de commentaires",
    "errorUploadingImage": "Erreur de téléchargement de l’image"
  }
}
</i18n>
